import { z } from 'zod';
import { BIOME_IDS } from './biomes.js';
import { MobIdSchema } from './mobs.js';
import { REGIONS } from './quests/regions.js';
/**
 * Quests (ADR 0047). Data, not code: who gives each one, what it asks, what
 * the townsfolk say and what it pays. One chain per region leads a newcomer
 * from its village to its boss, then on to the next region; the order is
 * the `requires` of each quest, never the order of this list.
 *
 * What a quest asks is read from what the server already keeps where it can
 * (kills and camps from the career, the bag, the worn pieces): only a word
 * with a townsperson and a place reached are written down, as flags of the
 * quest itself (`rules/quests.ts`).
 */
const Biome = z.enum(BIOME_IDS);
const Count = z.number().int().min(1).max(100);
/** A line of dialogue, French, as the townsperson says it. */
const Line = z.string().min(1).max(520);
/** Where a townsperson named without a zone stands: the open world. */
export const QUEST_NPC_DEFAULT_ZONE = 'world';
/**
 * A townsperson of a quest: that creature, standing in that region. The
 * smith of Havrebourg is `{ mob: 'smith', biome: 'meadow' }`, whichever
 * tile the generator gave him; a quest giver stands alone in his region.
 * `zone` sets apart the townsfolk of another zone (the camp's smith, ADR
 * 0066), who would otherwise answer for the world's.
 */
export const QuestNpcSchema = z.object({
    mob: MobIdSchema,
    biome: Biome,
    zone: z.string().min(1).optional(),
});
/** Every objective carries its French label, written for its grammar. */
const Label = z.string().min(1).max(60);
/**
 * Where a piece is to be had or worked (ADR 0078): the townsperson the map
 * and the arrow lead to while the objective is not met, rather than the one
 * who takes the quest back.
 */
const At = QuestNpcSchema.optional();
export const QuestObjectiveSchema = z.discriminatedUnion('kind', [
    /** Kills of these creatures since the quest was taken, summed (a party's kills count, ADR 0032). */
    z.object({
        kind: z.literal('kill'),
        label: Label,
        mobs: z.array(MobIdSchema).min(1),
        count: Count,
    }),
    /** Items in the bag at the turn-in, which takes them. */
    z.object({ kind: z.literal('collect'), label: Label, item: z.string().min(1), count: Count }),
    /** A word with a townsperson, who says `line` when it is had. */
    z.object({ kind: z.literal('talk'), label: Label, npc: QuestNpcSchema, line: Line }),
    /** Standing on a named place of the world (`landmarks`), once. */
    z.object({ kind: z.literal('reach'), label: Label, landmark: z.string().min(1) }),
    /** A village's portal used (ADR 0017), read from the portals the character has, before the quest or since. */
    z.object({ kind: z.literal('portal'), label: Label, portal: z.string().min(1) }),
    /** Camp chests of the region opened after their clear (ADR 0021), since the quest was taken. */
    z.object({ kind: z.literal('camp'), label: Label, biome: Biome, count: Count }),
    /** One of these pieces worn (a piece bought, or the same found rarer), cosmetics aside. */
    z.object({
        kind: z.literal('wear'),
        label: Label,
        items: z.array(z.string().min(1)).min(1),
        at: At,
    }),
    /**
     * A fight made to its end in an instance's zone (ADR 0068): the story's
     * stand in Shefira's Aire. Flagged like a place reached, when the room
     * says it was won; a death there leaves it unflagged.
     */
    z.object({ kind: z.literal('stand'), label: Label, zone: z.string().min(1).max(32) }),
    /**
     * Shefira slain in a raid (ADR 0068 §4, the Company's vow), since the quest
     * was taken: read from the career's `shefiraKills`, which her flight at the
     * end of the story never counts.
     */
    z.object({ kind: z.literal('raid'), label: Label, count: Count }),
    /** A worn piece at this smith's level or more (ADR 0013); with `items`, one of these pieces. */
    z.object({
        kind: z.literal('upgrade'),
        label: Label,
        level: z.number().int().min(1).max(10),
        items: z.array(z.string().min(1)).min(1).optional(),
        at: At,
    }),
]);
export const QuestRewardSchema = z.object({
    xp: z.number().int().min(0),
    gold: z.number().int().min(0),
    /** A piece of equipment comes bound to its character: a quest is not a shop. */
    items: z
        .array(z.object({ templateId: z.string().min(1), quantity: Count }))
        .max(4)
        .default([]),
});
export const QuestSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/),
    /** French, shown in the journal and the notices. */
    name: z.string().min(1).max(40),
    /** The region whose chain it belongs to. */
    biome: Biome,
    /**
     * The camp's introduction (ADR 0066): a short chain of its own before the
     * region's, left out of the regions' chains and of their share of experience.
     * Never abandoned (ADR 0078): what its giver hands over would come twice.
     */
    prologue: z.boolean().default(false),
    /** The level it is meant for, and the least it may be taken at. */
    level: z.number().int().min(1).max(30),
    /** Quests to have turned in before this one is offered. */
    requires: z.array(z.string().min(1)).default([]),
    giver: QuestNpcSchema,
    /** Who takes it back: often the giver, sometimes the one it sends you to. */
    turnIn: QuestNpcSchema,
    /** What the giver says to offer it. */
    offer: Line,
    /** A short line of what to do, in the journal. */
    summary: z.string().min(1).max(140),
    /** What the one who takes it back says while it is not done. */
    pending: Line,
    /** What they say as it is turned in. */
    complete: Line,
    objectives: z.array(QuestObjectiveSchema).min(1).max(5),
    /**
     * What the giver hands over with the quest, bound (a bait, ADR 0047),
     * taken back at the turn-in if some is left.
     */
    grants: z
        .array(z.object({ templateId: z.string().min(1), quantity: Count }))
        .max(2)
        .default([]),
    reward: QuestRewardSchema,
});
/** How many quests a character may hold at once. */
export const MAX_ACTIVE_QUESTS = 10;
/** How close to a townsperson a quest is taken, told or turned in, diagonals included. */
export const QUEST_REACH = 2;
const DEFINITIONS = REGIONS.flatMap((region) => region.quests);
export const QUESTS = Object.freeze(DEFINITIONS.map((raw) => QuestSchema.parse(raw)));
const BY_ID = new Map(QUESTS.map((quest) => [quest.id, quest]));
export function getQuest(id) {
    return BY_ID.get(id);
}
/** A region's chain, in the order of this file; the camp's prologue is not part of it. */
export function questsOf(biome) {
    return QUESTS.filter((quest) => quest.biome === biome && !quest.prologue);
}
/** The camp's introduction (ADR 0066), in its order. */
export function prologueQuests() {
    return QUESTS.filter((quest) => quest.prologue);
}
//# sourceMappingURL=quests.js.map